import * as R from 'ramda';
import { Map } from './models';


type keyName = string | number;


/**
 * Converts an array of objects to a map using one of the object's string properties as a key.
 *
 * @param objs - the array of objects to be added to the map
 * @param getKey - a function which selects the property to be used as a key; note that, if you wish to use a numeric or
 * other non-string type as your key, it must first be stringified
 */
export const toMap = <T>(
  objs: T[],
  getKey: (obj: T) => string,
): Map<T> => objs.reduce(
  (running, next) => R.assoc(getKey(next), next, running),
  {},
);


/**
 * Converts an array of objects to a map using one of the object's string properties as a key. Each object keeps a
 * record of its position in the source array (via a `sourceIndex` property).
 *
 * @param objs - the array of objects to be added to the map
 * @param getKey - a function which selects the property to be used as a key; note that, if you wish to use a numeric or
 * other non-string type as your key, it must first be stringified
 */
export const toIndexAwareMap = <T>(
  objs: T[],
  getKey: (obj: T) => string,
): Map<T & { sourceIndex: number }> => objs.reduce(
  (running, next, index) => R.assoc(getKey(next), { ...next, sourceIndex: index }, running),
  {},
);


/**
 * Curried function that takes an array and an index, then returns the item in the array at that index. If the index is
 * out of bounds, instead of returning `undefined`, this function will loop back around on the array, basically returning
 * the item at the remainder index of the array.
 *
 * This is useful if e.g. you're mapping an indefinite array of items to a finite list of colors, and you want to wrap
 * back around to the first color when you run out of colors.
 */
export const loopIndex = <T>(array: T[]) => (index: number): T => array[index % array.length];

